import * as Yup from 'yup';
// components
import { debounce } from 'lodash';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { m, AnimatePresence } from 'framer-motion';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
// mui
import {
  Box,
  Fab,
  Card,
  Chip,
  Alert,
  Paper,
  Stack,
  Button,
  Dialog,
  Tooltip,
  TextField,
  IconButton,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent,
  useMediaQuery,
  CircularProgress
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import linkAnimation from 'src/assets/lottie/link.json';
import { APP_SIDESHEET_Z_INDEX } from 'src/theme/z-index';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { useUploadFileMutation } from 'src/services/files/file.service';
import { getSendEmailErrorMessage } from 'src/services/email-sync/email-sync.utils';
import { restoreSingleMailerDialog, minimizeSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { MailerTargetType, EmailMessageTemplate, EmailConnectionStatus, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
// services
import { useSendSingleEmailMutation, useGetEmailConnectionStateQuery, useLazyGetTemplatedContentQuery, useGetEmailMessageTemplatesQuery } from 'src/services/email-sync/email-sync.service';

import Iconify from 'src/components/iconify';
import AnimationContainer from 'src/components/animation/container';
import FormProvider, { RHFEditor, RHFTextField } from 'src/components/hook-form';

// ----------------------------------------------------------------------

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// The mailer is fixed to the window, so without a cap a long draft grew the card off the top of
// the screen and took the To and Subject fields with it — a fixed card does not move with the page
// and had nothing of its own to scroll (JT1-1584). 90vh leaves a visible gap above a card that
// sits 8px off the bottom edge; the bulk mailer is capped the same way.
const MAILER_MAX_HEIGHT = '90vh';

// FormProvider renders a bare <form> with no style hook of its own, so the flex column that lets
// the scroll region claim the leftover height has to be applied to it from the parent.
const MAILER_FORM_FLEX_SX = {
  '& > form': {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
} as const;

const MAILER_FRAME_SX = {
  maxHeight: MAILER_MAX_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  ...MAILER_FORM_FLEX_SX,
} as const;

// `minHeight: 0` is what actually makes this scroll — a flex item defaults to min-content, so
// without it the region grows to fit the draft and pushes the card past its cap instead.
const MAILER_SCROLL_SX = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  px: 3,
  pb: 1,
} as const;

// ----------------------------------------------------------------------

interface EmailSenderDialogProps {
  open: boolean;
  onClose: () => void;
  emails?: string[];
  title?: string;
  templateType?: EmailMessageTemplateType;
  templateEntityId?: string;
  targetType?: MailerTargetType;
  targetId?: string;
}

export default function EmailSenderDialog({
  open,
  onClose,
  emails = [],
  title,
  templateType,
  templateEntityId,
  targetType,
  targetId
}: EmailSenderDialogProps) {

  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isClosing, setIsClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const sidesheetRef = useRef<HTMLDivElement>(null);

  const isMinimized = useAppSelector((state) => state.app.singleMailerDialog.minimized);

  const [recipients, setRecipients] = useState<string[]>(emails || []);
  const [newEmail, setNewEmail] = useState('');

  const [ccEmails, setCCEmails] = useState<string[]>([]);
  const [newCCEmail, setNewCCEmail] = useState('');

  const [emailError, setEmailError] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [templateSearchQuery, setTemplateSearchQuery] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailMessageTemplate | null>(null);

  // File attachment states
  const [attachments, setAttachments] = useState<{ file_id: string, filename: string }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentData: connectionState, isLoading: isLoadingConnection } = useGetEmailConnectionStateQuery();

  // Only an active connection renders a form, so the frame has to know before it decides whether to
  // wrap the scroll region and the pinned footer in one.
  const isConnectionActive = !isLoadingConnection && connectionState?.status === EmailConnectionStatus.ACTIVE;

  const {
    data: templatesData
  } = useGetEmailMessageTemplatesQuery({
    page: 1,
    per_page: 25,
    type: templateType,
    search_query: templateSearchQuery || undefined,
  }, {
    skip: !templateType,
  });

  const [sendEmail, { isLoading: isSendingMail }] = useSendSingleEmailMutation();
  const [getTemplatedContent, { isLoading: isGettingTemplatedContent }] = useLazyGetTemplatedContentQuery();
  const [uploadFile] = useUploadFileMutation();

  // schema
  const EmailFormSchema = Yup.object().shape({
    subject: Yup.string().required(t('email_sync.mailer.validation.subject_required')),
    body: Yup.string().required(t('email_sync.mailer.validation.body_required')),
  });

  // Form methods
  const methods = useForm({
    resolver: yupResolver(EmailFormSchema),
    defaultValues: {
      subject: '',
      body: '',
    },
  });

  const { handleSubmit, reset } = methods;

  // validate email format
  const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email);

  useEffect(() => {
    if (open) {
      setRecipients(emails || []);
      setCCEmails([]);
      setNewEmail('');
      setNewCCEmail('');
      setEmailError('');
      setErrorMessage(null);
      setSelectedTemplate(null);
      setAttachments([]);
      methods.reset({
        subject: '',
        body: '',
      });
    }
  }, [open, emails, methods]);

  // Add recipient email
  const handleAddRecipient = () => {
    if (!newEmail) {
      setEmailError(t('email_sync.mailer.validation.email_required'));
      return;
    }

    if (!validateEmail(newEmail)) {
      setEmailError(t('email_sync.mailer.validation.invalid_email_format'));
      return;
    }

    if (recipients.includes(newEmail)) {
      setEmailError(t('email_sync.mailer.validation.email_already_added'));
      return;
    }

    setRecipients([...recipients, newEmail]);
    setNewEmail('');
    setEmailError('');
    // Clears the footer alert raised by a submit with no recipient (JT1-1584).
    setErrorMessage(null);
  };

  // Remove recipient email
  const handleRemoveRecipient = (email: string) => {
    setRecipients(recipients.filter((e) => e !== email));
  };

  const handleAddCCRecipient = () => {
    if (!newCCEmail) {
      setEmailError(t('email_sync.mailer.validation.email_required'));
      return;
    }

    if (!validateEmail(newCCEmail)) {
      setEmailError(t('email_sync.mailer.validation.invalid_email_format'));
      return;
    }

    if (ccEmails.includes(newCCEmail)) {
      setEmailError(t('email_sync.mailer.validation.email_already_added'));
      return;
    }

    setCCEmails([...ccEmails, newCCEmail]);
    setNewCCEmail('');
    setEmailError('');
  }

  // Remove CC email
  const handleRemoveCCRecipient = (email: string) => {
    setCCEmails(ccEmails.filter((e) => e !== email));
  }

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    setIsUploading(true);

    try {
      const files = Array.from(event.target.files);

      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const response = await uploadFile({
          file,
          type: 'document'
        }).unwrap();

        setAttachments(prev => [
          ...prev,
          { file_id: response.id, filename: file.name }
        ]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      enqueueSnackbar(t('common.error_uploading_file'), {
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (fileId: string) => {
    setAttachments(attachments.filter(attachment => attachment.file_id !== fileId));
  };

  // The To and CC inputs are staging fields: an address only becomes a recipient once Enter or the
  // add button fires. Committing a valid pending address here stops Send rejecting (or silently
  // dropping) an address the user can see on screen. Submit-time errors go to the footer, because
  // on a long draft the fields themselves are scrolled out of sight (JT1-1584).
  const commitPendingEmail = (
    pending: string,
    existing: string[]
  ): { list: string[]; invalid?: boolean } => {
    const trimmed = pending.trim();

    if (!trimmed) {
      return { list: existing };
    }

    if (!validateEmail(trimmed)) {
      return { list: existing, invalid: true };
    }

    return { list: existing.includes(trimmed) ? existing : [...existing, trimmed] };
  };

  const onSubmit = async (data: { subject: string; body: string }) => {
    const pendingRecipients = commitPendingEmail(newEmail, recipients);
    const pendingCC = commitPendingEmail(newCCEmail, ccEmails);

    if (pendingRecipients.invalid || pendingCC.invalid) {
      setErrorMessage(t('email_sync.mailer.validation.invalid_email_format'));
      return;
    }

    const toEmails = pendingRecipients.list;
    const cc = pendingCC.list;

    setRecipients(toEmails);
    setCCEmails(cc);
    setNewEmail('');
    setNewCCEmail('');
    setEmailError('');

    if (toEmails.length === 0) {
      setErrorMessage(t('email_sync.mailer.validation.recipient_required'));
      return;
    }

    try {
      setErrorMessage(null);

      await sendEmail({
        subject: data.subject,
        body: data.body,
        to: toEmails,
        cc,
        target_type: targetType || undefined,
        target_id: targetId || undefined,
        attachments: attachments.length > 0 ? attachments.map(att => ({ file_id: att.file_id })) : undefined,
      }).unwrap();

      enqueueSnackbar(t('email_sync.api.send_email.success'), {
        variant: 'success',
      });

      reset();
      setRecipients([]);
      setAttachments([]);
      handleClose();
    } catch (err) {
      setErrorMessage(getSendEmailErrorMessage(err));
    }
  };

  const handleClose = useCallback(() => {
    if (!isMobile) {
      setIsClosing(true);
      setTimeout(() => {
        onClose();
        setIsClosing(false);
      }, 400);
    } else {
      onClose();
    }
  }, [isMobile, onClose]);

  // The desktop mailer is a plain card, not a Modal, so it gets no Esc handling for free — and it
  // now renders over surfaces that have their own (the record panel closes on Esc). Claim Esc while
  // the user is inside the mailer so it closes the draft, not the panel behind it (JT1-1583).
  useEffect(() => {
    if (isMobile || isMinimized || !open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!sidesheetRef.current?.contains(e.target as Node)) return;
      e.stopPropagation();
      handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, isMinimized, open, handleClose]);

  const handleMinimize = useCallback(() => {
    dispatch(minimizeSingleMailerDialog());
  }, [dispatch]);

  const handleRestore = useCallback(() => {
    dispatch(restoreSingleMailerDialog());
  }, [dispatch]);

  const viewConnectPage = useCallback(() => {
    handleClose();
    router.push(paths.dashboard.account.email);
  }, [router, handleClose]);

  const onTemplateSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setTemplateSearchQuery(value);
    }, 500),
    [setTemplateSearchQuery]
  );

  const handleTemplateSelect = useCallback(async (template: EmailMessageTemplate | null) => {
    if (!template) {
      setSelectedTemplate(null);
      methods.reset({
        subject: '',
        body: '',
      });
      return;
    }

    if (templateEntityId) {
      try {
        const { data } = await getTemplatedContent({
          templateId: template.id,
          entityId: templateEntityId
        });

        setSelectedTemplate(template);
        methods.reset({
          subject: data?.subject || '',
          body: data?.content || '',
        });
      }
      catch (error) {
        console.error('Error fetching template content:', error);
      }
    }

  }, [methods, getTemplatedContent, templateEntityId]);

  // render minimized version of the dialog
  const renderMinimized = () => (
    <Fab
      color="primary"
      variant='extended'
      aria-label="restore"
      onClick={handleRestore}
      data-app-sidesheet
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        // Same layer as the open mailer, so minimise/restore doesn't move it between layers.
        zIndex: APP_SIDESHEET_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 2,
        width: 'auto',
        borderRadius: '24px',
      }}
    >
      <OpenInFullIcon sx={{ fontSize: 20 }} />
      <Typography variant="button">
        {t('email_sync.mailer.restore_email')}
      </Typography>
    </Fab>
  );

  const renderContent = () => {
    if (isLoadingConnection) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (connectionState?.status !== EmailConnectionStatus.ACTIVE) {
      return (
        <Stack spacing={2} sx={{ p: 2 }} direction="column" alignItems="center">

          <AnimationContainer height={120} width={120} json={linkAnimation} loop />

          <Stack spacing={2} sx={{ p: 2 }} direction="column" alignItems="center" justifyContent="center">
            <Typography variant="body2" textAlign="center" sx={{ mb: 1 }}>
              {t('email_sync.mailer.connection_inactive')}
            </Typography>
            <Button
              variant="contained"
              endIcon={<Iconify icon="material-symbols:chat-paste-go-rounded" />}
              onClick={viewConnectPage}
            >
              {t('common.connect')}
            </Button>
          </Stack>
        </Stack>
      );
    }

    // Only the loading and disconnected states reach here: an active connection renders the
    // fields and the pinned footer directly, joined by the FormProvider (JT1-1584).
    return null;
  }

  // The fields scroll and the Send button is pinned, so the two render into different slots and
  // are joined by the FormProvider at each call site rather than here (JT1-1584).
  const renderEmailFields = () => (
    <Stack spacing={2}>

      {/* Recipients */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('email_sync.mailer.to')}:
        </Typography>

        {(emails.length === 0 || recipients.length === 0) && (
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={newEmail}
              variant='standard'
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder={t('email_sync.mailer.add_recipient_placeholder')}
              error={!!emailError}
              inputProps={{
                maxLength: 50, // Max email length
              }}
              helperText={emailError}
              sx={{ pr: 0 }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    disabled={isSendingMail || !newEmail}
                    onClick={handleAddRecipient}
                  >
                    <Iconify icon="mdi:email-plus" width={20} height={20} />
                  </IconButton>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddRecipient();
                }
              }}
            />
          </Stack>
        )}

        {recipients.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <RecipientDisplay
              recipients={recipients}
              handleRemoveRecipient={handleRemoveRecipient}
            />
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('email_sync.mailer.cc')}:
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={newCCEmail}
            variant='standard'
            onChange={(e) => setNewCCEmail(e.target.value)}
            placeholder={t('email_sync.mailer.add_recipient_placeholder')}
            inputProps={{
              maxLength: 50, // Max email length
            }}
            error={!!emailError}
            helperText={emailError}
            sx={{ pr: 0 }}
            InputProps={{
              endAdornment: (
                <IconButton
                  disabled={isSendingMail || !newCCEmail}
                  onClick={handleAddCCRecipient}
                >
                  <Iconify icon="mdi:email-plus" width={20} height={20} />
                </IconButton>
              ),
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddCCRecipient();
              }
            }}
          />
        </Stack>

        {ccEmails.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <RecipientDisplay
              recipients={ccEmails}
              handleRemoveRecipient={handleRemoveCCRecipient}
            />
          </Box>
        )}
      </Box>

      {
        templateType && (
          <Box>
            <Autocomplete
              multiple={false}
              options={templatesData?.results as EmailMessageTemplate[] ?? []}
              value={selectedTemplate as EmailMessageTemplate}
              // Search runs server-side via `search_query`; MUI must not filter the page again,
              // or templates outside the current page appear to be missing entirely.
              filterOptions={(options) => options}
              onInputChange={(_e, value) => onTemplateSearchQueryChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    'email_sync.mailer.template_search_label'
                  )}
                  placeholder={t(
                    'email_sync.mailer.template_search_placeholder'
                  )}
                />
              )}
              onChange={(_e, value: EmailMessageTemplate | null) => handleTemplateSelect(value)}
              renderOption={({ key, ...rest }, option: EmailMessageTemplate) => (
                <li key={key} {...rest}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${option.name}`}
                    </Typography>
                  </Stack>
                </li>
              )}
              getOptionLabel={(option) => `${option.name}` as string}
              isOptionEqualToValue={(option, value) =>
                option && (option as EmailMessageTemplate).id === (value as EmailMessageTemplate).id
              }
              getOptionKey={(option) => (option as EmailMessageTemplate).id as string}
            />
          </Box>
        )
      }

      {/* Subject */}
      <RHFTextField
        name="subject"
        label={t('email_sync.mailer.subject')}
        disabled={isGettingTemplatedContent || isSendingMail}
        InputProps={{
          endAdornment: (
            <>
              {
                isGettingTemplatedContent && (
                  <CircularProgress size={20} sx={{ ml: 1 }} />
                )
              }
            </>
          ),
        }}
      />

      {/* Email Body */}
      <RHFEditor
        name="body"
        label={t('email_sync.mailer.message')}
        placeholder={t('email_sync.mailer.message_placeholder')}
        disabled={isGettingTemplatedContent || isSendingMail}
        sx={{
          // StyledEditor clips to `overflow: hidden`, which makes it the toolbar's scrollport — a
          // sticky toolbar would then stick to an element that never scrolls, i.e. do nothing.
          // Releasing it hands the toolbar the card's scroll region instead; the two radius rules
          // below take over the corner clipping that `hidden` was doing.
          overflow: 'visible',
          '& .ql-editor': {
            minHeight: 120,
          },
          '& .ql-container.ql-snow': {
            borderColor: 'divider',
            overflow: 'hidden',
            borderBottomLeftRadius: 'inherit',
            borderBottomRightRadius: 'inherit',
          },
          // The editor caps at 640px, taller than the card's scroll region, so a toolbar in the
          // flow leaves the screen while the user is still typing in the body. The opaque
          // background is required, or the message scrolls through it.
          '& .ql-toolbar.ql-snow': {
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
          },
        }}
      />

      {/* File Attachments */}
      <Box>
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isSendingMail}
            size="small"
          >
            {t('email_sync.mailer.attach_files')}
          </Button>

          {isUploading && <CircularProgress size={24} />}
        </Stack>

        {attachments.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {attachments.map((attachment) => (
                <Chip
                  key={attachment.file_id}
                  label={attachment.filename}
                  onDelete={() => handleRemoveAttachment(attachment.file_id)}
                  deleteIcon={<CloseIcon fontSize="small" />}
                  size="small"
                  sx={{ maxWidth: '100%' }}
                />
              ))}
            </Box>
          </Paper>
        )}
      </Box>

    </Stack>
  );

  // Pinned below the scroll region so Send stays reachable however long the draft is. The error
  // alert rides with it for the same reason — an alert at the end of a scrolled draft is one the
  // user never sees.
  const renderEmailFooter = () => (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {errorMessage && (
        <Alert severity="error">
          {errorMessage}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        sx={{ width: '100%' }}
        disabled={isSendingMail}
        startIcon={isSendingMail ? <CircularProgress size={20} /> : null}
      >
        {t('email_sync.mailer.send_button')}
      </Button>
    </Stack>
  );

  // Mobile view - modal dialog
  if (isMobile) {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: MAILER_FORM_FLEX_SX }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {title || t('email_sync.mailer_sidesheet.title')}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton edge="end" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        {isConnectionActive ? (
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <DialogContent dividers data-testid="mailer-scroll-region">
              {renderEmailFields()}
            </DialogContent>
            <DialogActions>
              {renderEmailFooter()}
            </DialogActions>
          </FormProvider>
        ) : (
          <DialogContent dividers data-testid="mailer-scroll-region">
            {renderContent()}
          </DialogContent>
        )}
      </Dialog>
    );
  }

  // Desktop view - Floating card or minimized version
  return (
    <AnimatePresence mode="wait">
      {isMinimized && open ? (
        renderMinimized()
      ) : (open || isClosing) && (
        <m.div
          key="mailer-sidesheet"
          ref={sidesheetRef}
          data-app-sidesheet
          initial={{ y: 100, opacity: 0 }}
          animate={isClosing ? { y: 100, opacity: 0 } : { y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
            exit: { duration: 0.4 }
          }}
          style={{
            position: 'fixed',
            bottom: 8,
            right: 5,
            // Above the quick-view drawer and the record overlay, both of which used to bury the
            // mailer entirely (JT1-1583).
            zIndex: APP_SIDESHEET_Z_INDEX,
            maxWidth: 480,
            width: '100%',
          }}
        >
          <Card data-testid="mailer-card" sx={MAILER_FRAME_SX}>
            {/* Pinned: minimise and close stay reachable at any scroll depth. */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexShrink: 0, px: 3, pt: 3, pb: 2 }}
            >
              <Typography variant="h6">
                {title || t('email_sync.mailer_sidesheet.title')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <IconButton edge="end" onClick={handleMinimize}>
                  <Iconify icon="mingcute:minimize-fill" />
                </IconButton>
                <IconButton edge="end" onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>

            {isConnectionActive ? (
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Box data-testid="mailer-scroll-region" sx={MAILER_SCROLL_SX}>
                  {renderEmailFields()}
                </Box>
                <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 3 }}>
                  {renderEmailFooter()}
                </Box>
              </FormProvider>
            ) : (
              <Box data-testid="mailer-scroll-region" sx={MAILER_SCROLL_SX}>
                {renderContent()}
              </Box>
            )}
          </Card>
        </m.div>
      )}
    </AnimatePresence>
  );
}

const RecipientDisplay = ({ recipients, handleRemoveRecipient }: { recipients: string[], handleRemoveRecipient: (email: string) => void }) => {

  const { t } = useTranslation();

  const maxVisible = 1;
  const visibleRecipients = recipients.slice(0, maxVisible);
  const hiddenRecipients = recipients.slice(maxVisible);
  const hiddenCount = hiddenRecipients.length;

  return (
    <>
      {recipients.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {visibleRecipients.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={() => handleRemoveRecipient(email)}
              deleteIcon={<CloseIcon fontSize="small" />}
            />
          ))}
          {hiddenCount > 0 && (
            <Tooltip
              title={
                <Box sx={{ p: 1, width: '100%' }}>
                  {hiddenRecipients.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveRecipient(email)}
                      deleteIcon={<CloseIcon fontSize="small" />}
                      sx={{ mb: 0.5, mr: 0.5, width: '100%' }}
                    />
                  ))}
                </Box>
              }
              arrow
              placement="top"
            >
              <Typography
                sx={{
                  color: 'default',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                +{hiddenCount} {t('common.more')}
              </Typography>
            </Tooltip>
          )}
        </Box>
      )}
    </>
  );
};